/**
 * DXF mutations — delete + update metadata + rename category.
 *
 * Each mutation invalidates the broad ``["dxf"]`` key prefix so both the
 * file list and the categories sidebar refresh atomically — keeps the
 * surface from getting out of sync (e.g. deleting the last file in a
 * category should drop the category from the sidebar count).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  deleteDxfFile,
  renameDxfCategory,
  updateDxfMetadata,
  type UpdateDxfMetadataRequest,
} from "@ando/core";

const DXF_KEY_PREFIX = "dxf" as const;

function invalidateDxf(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: [DXF_KEY_PREFIX] });
}

export function useDeleteDxfFile() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (id) => deleteDxfFile(id),
    onSuccess: () => invalidateDxf(qc),
  });
}

export function useUpdateDxfMetadata() {
  const qc = useQueryClient();
  return useMutation<
    { success: boolean },
    Error,
    { id: string; body: UpdateDxfMetadataRequest }
  >({
    mutationFn: ({ id, body }) => updateDxfMetadata(id, body),
    onSuccess: () => invalidateDxf(qc),
  });
}

export function useRenameDxfCategory() {
  const qc = useQueryClient();
  return useMutation<
    { success: boolean; updated: number },
    Error,
    { oldName: string; newName: string }
  >({
    mutationFn: ({ oldName, newName }) => renameDxfCategory(oldName, newName),
    onSuccess: () => invalidateDxf(qc),
  });
}
